import React, { useCallback, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useHistory, useLocation } from 'react-router-dom';
import {
  Button,
  ConfirmationModal,
  FilterLabel,
  FilterReset,
  LoadingSpinner,
  Message,
  PagedTable,
  SelectOption,
  SortEvent,
  Th,
  ThSortable,
  formatDatetime,
  useBoundForm
} from 'common.ui';
import { accessRequestsClient, usersClient } from 'api/AdgangClients';
import {
  AdgangAccessControl,
  ApprovedAccessOrderBy,
  ApprovedAccessResponse,
  GenericValidationError,
  ResourceFilter,
  ResponseError,
  SortOrder,
  UserAccessType
} from 'api/adgang';
import Page from 'components/page/Page';
import { ReactComponent as IconDelete } from 'assets/icons/delete-icon.svg';
import { Col, Container, Row } from 'react-bootstrap';
import { useAccessControlList } from 'hooks/access/useAccessControlList';
import { useApplications } from 'api/hooks';
import { useRolesQuery } from 'api/hooks/useRolesQuery';
import { usePermissionsQuery } from 'api/hooks/usePermissionsQuery';

const PAGE_SIZE = 20;
const DEFAULT_SORT_FIELD = ApprovedAccessOrderBy.AssignedOn;
const DEFAULT_SORT_DIR = 'desc';

type SearchForm = {
  firstName: string;
  surname: string;
  applicationIds: string[];
  roleIds: string[];
  permissionIds: string[];
};

type UrlSearch = SearchForm & {
  page: string;
  sortField: string;
  sortDir: string;
};

const URL_DEFAULTS: UrlSearch = {
  firstName: '',
  surname: '',
  applicationIds: [],
  roleIds: [],
  permissionIds: [],
  page: '1',
  sortField: DEFAULT_SORT_FIELD,
  sortDir: DEFAULT_SORT_DIR
};

function useUrlState<T extends Record<string, string | string[]>>(defaults: T) {
  const location = useLocation();
  const history = useHistory();

  const current = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const out: Record<string, string | string[]> = { ...defaults };
    for (const key of Object.keys(defaults)) {
      if (Array.isArray(defaults[key as keyof T])) {
        // Tomme verdier fra URL-en ("applicationIds=") er ikke et filtervalg, og må ikke
        // bli et filter på tom streng.
        const vals = params.getAll(key).filter((v) => v !== '');
        if (vals.length > 0) out[key] = vals;
      } else {
        const v = params.get(key);
        if (v !== null) out[key] = v;
      }
    }
    return out as T;
  }, [location.search, defaults]);

  const set = useCallback(
    (next: Partial<T>) => {
      const params = new URLSearchParams(location.search);
      for (const [k, v] of Object.entries(next)) {
        params.delete(k);
        if (Array.isArray(v)) v.forEach((x) => params.append(k, String(x)));
        else if (v != null && v !== '') params.set(k, String(v));
      }
      history.replace({ ...location, search: params.toString() });
    },
    [location, history]
  );

  return [current, set] as const;
}

function hasFilters(s: SearchForm): boolean {
  return !!(s.firstName || s.surname || s.applicationIds.length || s.roleIds.length || s.permissionIds.length);
}

function ApprovedAccessRequests() {
  const [search, setSearch] = useUrlState<UrlSearch>(URL_DEFAULTS);
  const [formValues, setFormValues] = useState<SearchForm>(() => ({
    firstName: search.firstName,
    surname: search.surname,
    applicationIds: search.applicationIds,
    roleIds: search.roleIds,
    permissionIds: search.permissionIds
  }));
  const [formKey, setFormKey] = useState(0);
  const [removeAccess, setRemoveAccess] = useState<ApprovedAccessResponse | undefined>();

  const [, hasAccess] = useAccessControlList();
  const [applications] = useApplications(
    undefined,
    ResourceFilter.AccessRequest,
    !hasAccess(AdgangAccessControl.AccessRequestsViewShowNoAppFilter)
  );

  const { data: rolesData } = useRolesQuery({ applicationIds: search.applicationIds });
  const { data: permissionsData } = usePermissionsQuery({ applicationIds: search.applicationIds });

  const queryClient = useQueryClient();

  const currentPage = Number(search.page) || 1;
  const sorting = {
    field: (search.sortField || DEFAULT_SORT_FIELD) as ApprovedAccessOrderBy,
    direction: (search.sortDir || DEFAULT_SORT_DIR) as 'asc' | 'desc'
  };
  const startIndex = (currentPage - 1) * PAGE_SIZE;

  const { data, isLoading } = useQuery({
    queryKey: ['approvedAccess', search],
    queryFn: () =>
      accessRequestsClient.apiAccessRequestsApprovedPost({
        approvedAccessSearchRequest: {
          paginationQuery: {
            pageSize: PAGE_SIZE,
            pageNumber: currentPage,
            sortOrder: sorting.direction === 'asc' ? SortOrder.Ascending : SortOrder.Descending,
            orderField: sorting.field
          },
          firstName: search.firstName || undefined,
          surname: search.surname || undefined,
          roleIds: search.roleIds.map(Number),
          permissionIds: search.permissionIds.map(Number),
          applicationFilter: {
            applicationIds: search.applicationIds.filter((a) => a !== 'global'),
            includeGlobalValues: search.applicationIds.includes('global')
          }
        }
      }),
    keepPreviousData: true,
    staleTime: 30_000
  });

  const { form, FormContainer, Input, MultiSelect } = useBoundForm<SearchForm>({
    onSubmit: async (values) => {
      setFormValues(values);
      setSearch({ ...values, page: '1' });
    },
    model: formValues
  });

  function applyFilterRemoval(update: Partial<SearchForm>) {
    const next = { ...formValues, ...update };
    setFormValues(next);
    setFormKey((k) => k + 1);
    setSearch({ ...next, page: '1' });
  }

  function resetAll() {
    const empty: SearchForm = { firstName: '', surname: '', applicationIds: [], roleIds: [], permissionIds: [] };
    setFormValues(empty);
    setFormKey((k) => k + 1);
    setSearch({ ...empty, page: '1', sortField: DEFAULT_SORT_FIELD, sortDir: DEFAULT_SORT_DIR });
  }

  const handleSort = (e: SortEvent<ApprovedAccessOrderBy>) => {
    setSearch({ sortField: e.field, sortDir: e.direction, page: '1' });
  };

  const totalHits = data?.pageInfo?.totalCount ?? 0;

  return (
    <Page header='Tildelte rettigheter' title='Tildelte rettigheter'>
      <Container fluid>
        <FormContainer key={formKey} form={form}>
          <Row>
            <Col sm={12} lg={4}>
              <Input
                form={form}
                name='firstName'
                label='Fornavn'
                placeholder='Fornavn'
                onEnterPressed={(e) => {
                  const next = { ...formValues, firstName: e };
                  setFormValues(next);
                  setSearch({ ...next, page: '1' });
                }}
              />
            </Col>
            <Col sm={12} lg={4}>
              <Input
                form={form}
                name='surname'
                label='Etternavn'
                placeholder='Etternavn'
                onEnterPressed={(e) => {
                  const next = { ...formValues, surname: e };
                  setFormValues(next);
                  setSearch({ ...next, page: '1' });
                }}
              />
            </Col>
          </Row>
          <Row>
            <Col sm={12} lg={4}>
              <MultiSelect
                form={form}
                name='applicationIds'
                label='Applikasjon'
                okLabel='Vis resultater'
                options={(applications as SelectOption[]) ?? []}
                onChange={(e) => {
                  const next = { ...formValues, applicationIds: e, roleIds: [], permissionIds: [] };
                  setFormValues(next);
                  setSearch({ ...next, page: '1' });
                }}
              />
            </Col>
            <Col sm={12} lg={4}>
              <MultiSelect
                form={form}
                name='roleIds'
                label='Rolle'
                okLabel='Vis resultater'
                options={rolesData ?? []}
                onChange={(e) => {
                  const next = { ...formValues, roleIds: e };
                  setFormValues(next);
                  setSearch({ ...next, page: '1' });
                }}
              />
            </Col>
            <Col sm={12} lg={4}>
              <MultiSelect
                form={form}
                name='permissionIds'
                label='Tilgang'
                okLabel='Vis resultater'
                options={permissionsData ?? []}
                onChange={(e) => {
                  const next = { ...formValues, permissionIds: e };
                  setFormValues(next);
                  setSearch({ ...next, page: '1' });
                }}
              />
            </Col>
          </Row>
          <Row>
            <Col sm={12}>
              <br />
              {search.firstName && (
                <FilterLabel
                  key='firstName'
                  label={search.firstName}
                  onRemove={() => applyFilterRemoval({ firstName: '' })}
                />
              )}
              {search.surname && (
                <FilterLabel
                  key='surname'
                  label={search.surname}
                  onRemove={() => applyFilterRemoval({ surname: '' })}
                />
              )}
              {search.applicationIds.map((a) => (
                <FilterLabel
                  key={`app-${a}`}
                  label={(applications as SelectOption[])?.find((opt) => opt.id === a)?.text ?? a}
                  onRemove={() =>
                    applyFilterRemoval({
                      applicationIds: search.applicationIds.filter((id) => id !== a),
                      roleIds: [],
                      permissionIds: []
                    })
                  }
                />
              ))}
              {search.roleIds.map((r) => (
                <FilterLabel
                  key={`role-${r}`}
                  label={rolesData?.find((opt) => opt.id === r)?.text ?? r}
                  onRemove={() => applyFilterRemoval({ roleIds: search.roleIds.filter((id) => id !== r) })}
                />
              ))}
              {search.permissionIds.map((p) => (
                <FilterLabel
                  key={`perm-${p}`}
                  label={permissionsData?.find((opt) => opt.id === p)?.text ?? p}
                  onRemove={() =>
                    applyFilterRemoval({ permissionIds: search.permissionIds.filter((id) => id !== p) })
                  }
                />
              ))}
              {hasFilters(search) && <FilterReset key='reset' label='Fjern alle' onRemove={resetAll} />}
            </Col>
          </Row>
          <Row className='pt-4'>
            <Col sm={12}>
              <Button type='submit' text='Søk' />
            </Col>
          </Row>
        </FormContainer>

        {isLoading ? (
          <LoadingSpinner />
        ) : (
          <PagedTable
            startIndex={startIndex}
            totalHits={totalHits}
            pageSize={PAGE_SIZE}
            onNextClick={() => setSearch({ page: String(currentPage + 1) })}
            onPreviousClick={() => setSearch({ page: String(currentPage - 1) })}
          >
            <thead>
              <tr>
                <ThSortable
                  title='Navn'
                  currentSort={sorting}
                  onSort={handleSort}
                  field={ApprovedAccessOrderBy.UserDisplayName}
                />
                <ThSortable
                  title='E-postadresse'
                  currentSort={sorting}
                  onSort={handleSort}
                  field={ApprovedAccessOrderBy.UserEmail}
                />
                <ThSortable
                  title='Telefonnummer'
                  currentSort={sorting}
                  onSort={handleSort}
                  field={ApprovedAccessOrderBy.UserMfaPhone}
                />
                <ThSortable
                  title='Rettighet/Organisasjon'
                  currentSort={sorting}
                  onSort={handleSort}
                  field={ApprovedAccessOrderBy.AccessName}
                />
                <ThSortable
                  title='Applikasjonsnavn'
                  currentSort={sorting}
                  onSort={handleSort}
                  field={ApprovedAccessOrderBy.ApplicationName}
                />
                <ThSortable
                  title='Dato'
                  currentSort={sorting}
                  onSort={handleSort}
                  field={ApprovedAccessOrderBy.AssignedOn}
                />
                <Th title='Fjern' />
              </tr>
            </thead>
            <tbody>
              {data?.data?.map((u) => (
                <tr key={`${u.accessType}${u.accessId}${u.userId}${u.organizationId}`}>
                  <td>{u.userDisplayName}</td>
                  <td>{u.userEmail}</td>
                  <td>{u.userMfaPhone}</td>
                  <td>
                    {u.accessName}
                    {u.organizationName ? ` / ${u.organizationName}` : ''}
                  </td>
                  <td>{u.applicationName}</td>
                  <td>{u.assignedOn && formatDatetime(u.assignedOn, true)}</td>
                  <td>
                    <IconDelete onClick={() => setRemoveAccess(u)} />
                  </td>
                </tr>
              ))}
            </tbody>
          </PagedTable>
        )}

        <RemoveAccessModal
          accessRequest={removeAccess}
          onDeleteCancelled={() => setRemoveAccess(undefined)}
          onDeleteSuccess={() => {
            setRemoveAccess(undefined);
            queryClient.invalidateQueries(['approvedAccess']);
          }}
        />
      </Container>
    </Page>
  );
}

function RemoveAccessModal({
  accessRequest,
  onDeleteCancelled,
  onDeleteSuccess
}: {
  accessRequest: ApprovedAccessResponse | undefined;
  onDeleteCancelled: () => void;
  onDeleteSuccess: () => void;
}) {
  const [error, setError] = useState<GenericValidationError | undefined>(undefined);
  const [deleteLoading, setDeleteLoading] = useState<boolean>(false);

  const removeUserRight = async (toDelete: ApprovedAccessResponse): Promise<void> => {
    if (toDelete.accessType === UserAccessType.Role && toDelete.organizationId) {
      await usersClient.apiUsersUserIdRolesRoleIdOrganizationOrganizationIdDelete({
        roleId: toDelete.accessId as number,
        userId: toDelete.userId as string,
        organizationId: toDelete?.organizationId
      });
    } else if (toDelete.accessType === UserAccessType.Permission && toDelete.organizationId) {
      await usersClient.apiUsersUserIdPermissionsPermissionIdOrganizationOrganizationIdDelete({
        permissionId: toDelete.accessId as number,
        userId: toDelete.userId as string,
        organizationId: toDelete?.organizationId
      });
    } else if (toDelete.accessType === UserAccessType.Permission) {
      await usersClient.apiUsersUserIdPermissionsPermissionIdDelete({
        permissionId: toDelete.accessId as number,
        userId: toDelete.userId as string
      });
    } else if (toDelete.accessType === UserAccessType.Role) {
      await usersClient.apiUsersUserIdRolesRoleIdDelete({
        roleId: toDelete.accessId as number,
        userId: toDelete?.userId as string
      });
    }
  };

  async function onDeleteConfirmed() {
    if (accessRequest) {
      setDeleteLoading(true);
      let isError = false;
      try {
        setError(undefined);
        await removeUserRight(accessRequest);
      } catch (e) {
        isError = true;
        if (e instanceof ResponseError && e.response.status === 400) {
          const apiError = (await e.response.json()) as GenericValidationError;
          if (apiError) {
            setError(apiError);
          }
        }
      } finally {
        setDeleteLoading(false);
        if (!isError) onDeleteSuccess();
      }
    }
  }

  return (
    <ConfirmationModal
      header='Fjern rettighet fra brukeren'
      show={!!accessRequest}
      onCancel={onDeleteCancelled}
      onAccept={onDeleteConfirmed}
      disabled={error !== undefined}
      hideButtons={deleteLoading}
    >
      {!error && (
        <>
          <p> Er du sikker på at du vil fjerne rettighet fra brukeren? </p>
          <div>
            <div>
              Bruker: <strong>{accessRequest?.userDisplayName}</strong>
            </div>
            <div>
              Rettighet: <strong>{accessRequest?.accessName}</strong>
            </div>
            <div>
              Applikasjon: <strong>{accessRequest?.applicationName}</strong>
            </div>
            {accessRequest?.organizationId && (
              <div>
                På vegne av: <strong>{accessRequest?.organizationName}</strong>
              </div>
            )}
          </div>
        </>
      )}
      {deleteLoading && <LoadingSpinner />}
      {error?.messages && error.messages.length > 0 && (
        <Message key='app_delete_err' text={error.messages[0]} type='error' />
      )}
    </ConfirmationModal>
  );
}

export default ApprovedAccessRequests;
